/**
 * The shared TA roster. One document on the server, not per period — see `lib/taCockpitTeam.js`.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { StoredTeam, TeamMember } from '../types/taTeam';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL || 'https://proxy-prod-people-520260363689.europe-west3.run.app';

export async function fetchTeam(): Promise<StoredTeam | null> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/ta-cockpit/team`);
  if (!res.ok) throw new Error(`Could not read the team (${res.status})`);
  const body = (await res.json()) as { team: StoredTeam | null };
  return body.team;
}

/** Thrown when the server refuses the roster — the message is for the person editing it. */
export class TeamRejectedError extends Error {}

export async function saveTeam(members: TeamMember[], note?: string): Promise<StoredTeam> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/ta-cockpit/team`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ members, note }),
    /*
     * ⚠️ Without this, `authenticatedFetch` THROWS on a 400 and the branch below never runs — so a
     * refusal the person editing could act on ("Anna: hours must be a number") would surface as a
     * generic request failure instead of the message the server wrote for them.
     */
    suppressErrorStatuses: [400],
  });
  if (res.status === 400) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new TeamRejectedError(body.error || 'The team could not be saved');
  }
  if (!res.ok) throw new Error(`Could not save the team (${res.status})`);
  const body = (await res.json()) as { team: StoredTeam };
  return body.team;
}
